/**
 * AuthContext — wraps Amplify's email/password sign-in (FE1 — login + shell).
 *
 * Login-only: catbento uses admin-provisioned accounts (no self-signup), so the
 * surface is just `signIn` / `signOut` (+ `refresh`). No sign-up / verify /
 * forgot-reset.
 *
 * Identity is read straight from the Cognito **ID token** in the current Amplify
 * session (`sub` + `email`) — no `/me` call. The ID token is also what api.ts
 * attaches as the bearer: its `aud` = our app-client id, exactly what the JWT
 * authorizer validates. Ported from bento's app-web AuthContext.
 */

import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  fetchAuthSession,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { AMPLIFY_CONFIGURED } from "../lib/amplify.js";

/** The signed-in user, from the ID-token claims. No server record. */
export interface AuthUser {
  userId: string; // Cognito `sub`
  email: string;
}

interface AuthState {
  /** Null while loading and for anonymous (not-signed-in) callers. */
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/** Read {userId, email} from the current session's ID token, or null if none. */
async function readSessionUser(): Promise<AuthUser | null> {
  const session = await fetchAuthSession();
  const claims = session.tokens?.idToken?.payload;
  if (!claims) return null;
  const sub = typeof claims.sub === "string" ? claims.sub : null;
  const email = typeof claims.email === "string" ? claims.email : null;
  if (!sub || !email) return null;
  return { userId: sub, email };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      setUser(await readSessionUser());
    } catch {
      // fetchAuthSession throws when there's no session — treated as anonymous.
      setUser(null);
    }
  };

  // Initial session load. On an unconfigured build (no Cognito ids) skip Amplify
  // entirely and render as anonymous.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!AMPLIFY_CONFIGURED) {
        setLoading(false);
        return;
      }
      try {
        const me = await readSessionUser();
        if (!cancelled) setUser(me);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Track Amplify's own auth events (token refresh, sign-out in another tab).
  useEffect(() => {
    if (!AMPLIFY_CONFIGURED) return;
    return Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signedIn" || payload.event === "tokenRefresh") {
        void refresh();
      } else if (payload.event === "signedOut") {
        setUser(null);
      }
    });
  }, []);

  const signIn = async (email: string, password: string) => {
    await amplifySignIn({ username: email, password });
    await refresh();
  };

  const signOut = async () => {
    if (AMPLIFY_CONFIGURED) await amplifySignOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
